'use client';

import React from 'react';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { cm } from 'src/utils/classNames';
import type { WithClassName } from 'src/utils/UtilityTypes';
import { signDependentValue } from 'src/utils/signDependentValue';
import type {
	FormatNumberValue,
	FormatNumberOptions,
} from 'src/utils/formatNumber/types';
import useNumberDisplay from 'src/components/NumberDisplay/hooks/useNumberDisplay';

export interface NumberDisplayProps extends WithClassName, FormatNumberOptions {
	value: FormatNumberValue;
	/**
	 * Shows `*****` when `true`.
	 */
	isAccountValueToHide?: boolean;
	/**
	 * Shows a skeleton placeholder when `true`.
	 *
	 * You should also use `skeletonClassName` to apply width / height.
	 */
	loading?: boolean;
	skeletonClassName?: string;
	/**
	 * Applies color to the value based on its sign (i.e. - / +) when `true`.
	 */
	signDependentColor?: boolean;
	/**
	 * Override the formatted value with a custom string.
	 *
	 * Note, this takes precedence over `fallback`.
	 */
	displayOverride?: string;
	/**
	 * Applies `opacity-50` to the value when `true`.
	 */
	dimIfZero?: boolean;
}

export default function NumberDisplay({
	value,
	type,
	millify,
	decimals,
	precision,
	trimZeroes,
	suffix,
	surround,
	signDependentColor,
	isAccountValueToHide,
	dimIfZero,
	loading,
	skeletonClassName,
	displayOverride,
	fallback,
	className,
}: NumberDisplayProps) {
	const { formattedValue, showAccountValues } = useNumberDisplay({
		value,
		type,
		millify,
		decimals,
		precision,
		trimZeroes,
		suffix,
		surround,
		fallback,
	});

	if (loading) {
		return <SkeletonValuePlaceholder className={skeletonClassName} loading />;
	}

	if (isAccountValueToHide && !showAccountValues) {
		return <span className={className}>*****</span>;
	}

	const displayValue = displayOverride ?? formattedValue;

	return (
		<span
			className={cm(
				'whitespace-nowrap',
				dimIfZero && value?.eqZero() && 'opacity-50',
				signDependentColor &&
					signDependentValue(value?.toNum(), {
						positive: 'text-text-positive-green-button',
						negative: 'text-text-negative-red-button',
						zero: 'text-text-default',
						undefined: '',
					}),
				className
			)}
		>
			{displayValue}
		</span>
	);
}
